import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

/**
 * Run ELK layered layout (LEFT→RIGHT) and return node positions.
 * Pure geometry helper — no workflow métier types.
 *
 * Edges are wired node→node (not port→port). Persisted React Flow handles are often
 * bottom→top; feeding those into ELK with direction RIGHT forces a diagonal staircase.
 * Positions only — RF keeps drawing with whatever handles are stored.
 *
 * @param {{
 *   nodes: Array<{ id: string, width: number, height: number, portHandles?: string[] }>,
 *   edges: Array<{ id?: string, source: string, target: string, sourceHandle?: string, targetHandle?: string }>,
 * }} graph
 * @returns {Promise<Record<string, { x: number, y: number }>>}
 */
export async function computeElkLayeredPositions(graph) {
    const children = (graph.nodes || []).map((node) => ({
        id: String(node.id),
        width: Number(node.width) > 0 ? Number(node.width) : 1,
        height: Number(node.height) > 0 ? Number(node.height) : 1,
    }));

    const id_set = new Set(children.map((c) => c.id));
    const edges = (graph.edges || [])
        .filter((e) => id_set.has(String(e.source)) && id_set.has(String(e.target)) && String(e.source) !== String(e.target))
        .map((e, i) => ({
            id: String(e.id || `elk-e-${i}`),
            sources: [String(e.source)],
            targets: [String(e.target)],
        }));

    const layouted = await elk.layout({
        id: 'workflow-root',
        layoutOptions: {
            'elk.algorithm': 'layered',
            'elk.direction': 'RIGHT',
            'elk.edgeRouting': 'ORTHOGONAL',
            'elk.spacing.nodeNode': '50',
            'elk.layered.spacing.nodeNodeBetweenLayers': '80',
            'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
            'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
            // Keep the main chain centered so fan-out/fan-in stay symmetric around the axis.
            'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
        },
        children,
        edges,
    });

    const positions = {};
    (layouted.children || []).forEach((child) => {
        positions[String(child.id)] = {
            x: Number(child.x) || 0,
            y: Number(child.y) || 0,
        };
    });
    return positions;
}
